import React, { useState } from 'react';
import { Modal } from "../../../components/ui/modal";
import Button from "../../../components/ui/button/Button";
import Input from "../../../components/form/input/InputField";
import Select from "../../../components/form/Select";
import { Calendar, DollarSign, FileText, AlertCircle, ClipboardList } from 'lucide-react';
import useApi from '../../../hooks/useApi';
import { toast } from 'react-hot-toast';

interface CreateIngresoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

interface FormErrors {
  fecha?: string;
  monto?: string;
  clienteNombre?: string;
  descripcion?: string;
}

const CreateIngresoModal: React.FC<CreateIngresoModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { createIngreso } = useApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  
  // Form state
  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0], // Current date as default
    monto: '',
    clienteNombre: '',
    estado: 'completo',
    metodoPago: 'efectivo',
    descripcion: ''
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.fecha) {
      newErrors.fecha = 'Por favor, seleccione una fecha válida';
    }
    
    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      newErrors.monto = 'Ingrese un monto válido mayor a 0';
    }
    
    if (!formData.clienteNombre.trim()) {
      newErrors.clienteNombre = 'El nombre del origen es requerido';
    }
    
    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es requerida';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value: string, name: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create the ingreso object
      const ingresoData = {
        fecha: formData.fecha,
        monto: parseFloat(formData.monto),
        clienteNombre: formData.clienteNombre,
        estado: formData.estado,
        metodoPago: formData.metodoPago,
        descripcion: formData.descripcion
      };
      
      await createIngreso(ingresoData);
      
      toast.success('Ingreso registrado exitosamente');
      
      // Reset form and close modal
      setFormData({
        fecha: new Date().toISOString().split('T')[0],
        monto: '',
        clienteNombre: '',
        estado: 'completo',
        metodoPago: 'efectivo',
        descripcion: ''
      });
      
      // Refresh data
      if (onSuccess) {
        await onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error al registrar ingreso:', error);
      toast.error('Error al registrar el ingreso. Por favor, inténtelo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Registrar nuevo ingreso">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Fecha del ingreso */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <Calendar className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
              Fecha
              <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              name="fecha"
              value={formData.fecha}
              onChange={handleInputChange}
              onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
              placeholder="Seleccionar fecha"
              className="w-full"
              error={errors.fecha ? true : false}
              hint={errors.fecha}
            />
          </div>

          {/* Monto del ingreso */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <DollarSign className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
              Monto
              <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              name="monto"
              value={formData.monto}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              error={errors.monto ? true : false}
              hint={errors.monto}
              className="w-full"
            />
          </div>
        </div>

        {/* Nombre del origen del ingreso */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <AlertCircle className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
            Origen del Ingreso
            <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            name="clienteNombre"
            value={formData.clienteNombre}
            onChange={handleInputChange}
            placeholder="Nombre del cliente o entidad"
            error={errors.clienteNombre ? true : false}
            hint={errors.clienteNombre}
            className="w-full"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Especifique el origen de este ingreso (cliente, empresa, entidad, etc.)
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Estado del ingreso */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <FileText className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
              Estado
            </label>
            <Select
              options={[
                { value: 'completo', label: 'Completo' },
                { value: 'parcial', label: 'Parcial' }
              ]}
              defaultValue={formData.estado}
              onChange={(value) => handleSelectChange(value, 'estado')}
              placeholder="Seleccionar estado"
              className="w-full"
            />
          </div>

          {/* Método de pago */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <FileText className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
              Método de Pago
            </label>
            <Select
              options={[
                { value: 'efectivo', label: 'Efectivo' },
                { value: 'tarjeta', label: 'Tarjeta' },
                { value: 'transferencia', label: 'Transferencia bancaria' },
                { value: 'cheque', label: 'Cheque' },
                { value: 'deposito', label: 'Depósito bancario' },
                { value: 'credito', label: 'Crédito' },
                { value: 'otro', label: 'Otro' }
              ]}
              defaultValue={formData.metodoPago}
              onChange={(value) => handleSelectChange(value, 'metodoPago')}
              placeholder="Seleccionar método"
              className="w-full"
            />
          </div>
        </div>

        {/* Descripción */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <ClipboardList className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
            Descripción
            <span className="text-red-500">*</span>
          </label>
          <textarea
            name="descripcion"
            value={formData.descripcion}
            onChange={handleInputChange}
            placeholder="Descripción detallada del ingreso"
            rows={3}
            className={`w-full px-3 py-2 border rounded-md ${
              errors.descripcion 
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
            } dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
          />
          {errors.descripcion && (
            <p className="text-xs text-red-500">{errors.descripcion}</p>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Detalle el propósito, referencia o contexto de este ingreso
          </p>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="px-4"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="px-4"
          >
            {isSubmitting ? 'Registrando...' : 'Registrar Ingreso'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateIngresoModal;